import React, { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { State } from "state";
import { getRoles } from "state/chain_user_role";
import Constants from "helpers/constants";

export interface UserStore {
  retail_store_num: any;
}

export interface NotAssociatedUser {
  id: number | string;
  email: string;
  selected: boolean;
  role: string;
  chain?: string;
}

interface UsersNotFoundModalProps {
  isOpen: boolean;
  users: any[];
  chainName?: string;
  getRoles: () => void;
  onSave: (usersWithRoles: { email: string; role: string; chain?: string }[]) => void;
  onNo: () => void;
  onClose: () => void;
}

interface LocalUsersNotFoundModalProps {
  isOpen: boolean;
  users: any[];
  chainName?: string;
  onSave: (usersWithRoles: { email: string; role: string; chain?: string }[]) => void;
  onNo: () => void;
  onClose: () => void;
  isManagedData?: boolean;
  errors?: any[];
  closeOnBackdropClick?: boolean;
}

type CombinedProps = LocalUsersNotFoundModalProps & PropsFromRedux;

const AddUsersNotFoundModal: React.FC<CombinedProps> = ({
  isOpen,
  users,
  onSave,
  onNo,
  onClose,
  getRoles,
  roles,
  isManagedData,
  errors,
  closeOnBackdropClick = true,
}) => {
  const hasApiErrors = !!(errors && errors.length > 0);
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && !hasApiErrors && e.target === e.currentTarget) {
      onClose();
    }
  };

  const dispatch = useDispatch();
  const [usersList, setUsersList] = useState<NotAssociatedUser[]>([]);
  // const [selectAll, setSelectAll] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: number]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [noSelectionError, setNoSelectionError] = useState(false);

  // useEffect(() => {
  //   const styleId = "custom-checkbox-style";
  //   if (!document.getElementById(styleId)) {
  //     const style = document.createElement("style");
  //     style.id = styleId;
  //     style.textContent = `input[type="checkbox"].custom-checkbox { accent-color: #E5B42F; }`;
  //     document.head.appendChild(style);
  //   }
  // }, []);

  useEffect(() => { getRoles(); }, []);

  useEffect(() => {
    if (users && users.length > 0) {
      setUsersList(
        users.map((userObject: any, index) => ({
          chain: userObject?.chain,
          id: userObject.id || index,
          email: userObject.email,
          role: "",
          selected: true,
        }))
      );
      setIsSaving(false);
      setValidationErrors({});
      setNoSelectionError(false);
    }
  }, [users]);
  
  useEffect(() => {
    if (errors && errors.length > 0) {
      setIsSaving(false);
    }
  }, [errors]);

  if (!isOpen) return null;

  // const handleSelectAll = () => {
  //   const newSelectAll = !selectAll;
  //   setSelectAll(newSelectAll);
  //   setUsersList(usersList.map((user) => ({ ...user, selected: newSelectAll })));
  // };

  // const handleUserSelect = (index: number) => {
  //   const newUsersList = [...usersList];
  //   newUsersList[index].selected = !newUsersList[index].selected;
  //   setUsersList(newUsersList);
  //   setSelectAll(newUsersList.every((user) => user.selected));
  //   if (newUsersList[index].selected) setNoSelectionError(false);
  //   if (!newUsersList[index].selected) {
  //     const newErrors = { ...validationErrors };
  //     delete newErrors[index];
  //     setValidationErrors(newErrors);
  //   }
  // };

  const handleRoleChange = (index: number, role: string) => {
    const newUsersList = [...usersList];
    newUsersList[index].role = role;
    setUsersList(newUsersList);
    if (role) {
      const newErrors = { ...validationErrors };
      delete newErrors[index];
      setValidationErrors(newErrors);
    }
  };

  const handleSave = async () => {
    const errs: { [key: number]: boolean } = {};
    let hasErrors = false;
    usersList.forEach((user, index) => {
      if (!user.role) { errs[index] = true; hasErrors = true; }
    });
    if (hasErrors) { setValidationErrors(errs); return; }
    const selectedUsers = usersList
      .filter((user) => user.role)
      .map((user) => ({ email: user.email, role: user.role, chain_name: user.chain }));
    // if (selectedUsers.length === 0) { setNoSelectionError(true); return; }
    // setNoSelectionError(false);
    setIsSaving(true);
    try {
      await onSave(selectedUsers);
    } catch (error) {
      console.error("Error saving users:", error);
      setIsSaving(false);
    }
  };

  // Grid columns adapt based on isManagedData
  const gridCols = isManagedData ? "1fr 1fr 1fr" : "1fr 1fr";

  return (
    <>
      <style>{`
        .aunf-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
          box-sizing: border-box;
        }
        .aunf-modal {
          background-color: #fff;
          border-radius: 16px;
          padding: 32px;
          max-width: 900px;
          width: 100%;
          position: relative;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          max-height: 90vh;
          overflow-y: auto;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        }
        .aunf-close-btn {
          position: absolute;
          top: 16px; right: 16px;
          background: none; border: none;
          font-size: 28px; cursor: pointer;
          color: #000; line-height: 1; padding: 4px;
        }
        .aunf-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
          padding-right: 36px;
        }
        .aunf-icon {
          width: 48px; height: 48px; min-width: 48px;
          background-color: #F5A623;
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
        }
        .aunf-title {
          font-size: 24px; font-weight: 700;
          color: #000; margin: 0 0 8px 0;
        }
        .aunf-message {
          font-size: 16px; color: #666;
          line-height: 1.5; margin: 0;
        }

        /* Table */
        .aunf-table-container {
          margin: 24px 0;
          overflow-x: auto;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }
        .aunf-table-inner {
          min-width: 480px;
        }
        .aunf-table-header {
          display: grid;
          grid-template-columns: ${gridCols};
          gap: 12px;
          padding: 12px 16px;
          background-color: #F9FAFB;
          border-radius: 8px 8px 0 0;
          align-items: center;
        }
        .aunf-table-row {
          display: grid;
          grid-template-columns: ${gridCols};
          gap: 12px;
          padding: 14px 16px;
          align-items: center;
          border-bottom: 1px solid #E5E7EB;
        }
        .aunf-table-row:last-child {
          border-bottom: none;
        }
        .aunf-header-text {
          font-size: 14px; font-weight: 600;
          color: #6B7280; margin: 0;
        }
        .aunf-cell-text {
          font-size: 14px; color: #374151;
          margin: 0; word-break: break-word;
        }
        .aunf-select {
          padding: 8px 36px 8px 12px;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          font-size: 14px;
          color: #6B7280;
          cursor: pointer;
          background-color: white;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          width: 100%;
        }
        .aunf-select-error {
          border-color: #EF4444 !important;
          border-width: 2px !important;
        }
        .aunf-error-msg {
          font-size: 12px; color: #EF4444;
          margin: 4px 0 0 0;
        }
        .aunf-checkbox {
          width: 18px; height: 18px;
          cursor: pointer;
          accent-color: #E5B42F;
          flex-shrink: 0;
        }

        /* API errors */
        .aunf-api-errors {
          max-height: 50px;
          overflow-y: auto;
          margin-bottom: 4px;
        }
        .aunf-api-error-item {
          color: #D32F2F; font-size: 12px; margin-bottom: 4px;
        }

        /* Buttons */
        .aunf-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          flex-wrap: wrap;
        }
        .aunf-btn {
          padding: 12px 32px;
          font-size: 16px; font-weight: 600;
          border-radius: 8px; cursor: pointer;
          border: 2px solid #F5A623;
          transition: all 0.2s ease;
          min-width: 90px;
        }
        .aunf-btn-save {
          background-color: #F5A623; color: #fff;
        }
        .aunf-btn-no {
          background-color: #fff; color: #000;
        }
        .aunf-btn:disabled {
          opacity: 0.6; cursor: not-allowed;
        }

        /* ── Tablet ── */
        @media (max-width: 768px) {
          .aunf-modal { padding: 24px; }
          .aunf-title { font-size: 20px; }
          .aunf-message { font-size: 14px; }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          .aunf-modal { padding: 16px; border-radius: 10px; }
          .aunf-close-btn { top: 12px; right: 12px; font-size: 24px; }
          .aunf-header { gap: 10px; margin-bottom: 16px; }
          .aunf-icon { width: 36px; height: 36px; min-width: 36px; }
          .aunf-icon svg { width: 18px; height: 18px; }
          .aunf-title { font-size: 17px; }
          .aunf-message { font-size: 13px; }
          .aunf-btn {
            flex: 1;
            padding: 10px 16px;
            font-size: 14px;
            text-align: center;
          }
          .aunf-buttons { justify-content: stretch; }
        }
      `}</style>

      <div onClick={handleBackdropClick}>
        <div className="aunf-overlay" onClick={isSaving ? undefined : onClose}>
          <div className="aunf-modal" onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button
              className="aunf-close-btn"
              style={{ opacity: isSaving ? 0.4 : 1, cursor: isSaving ? "not-allowed" : "pointer" }}
              onClick={onClose}
              disabled={isSaving}
            >
              ×
            </button>

            {/* Header */}
            <div className="aunf-header">
              <div className="aunf-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="white" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h2 className="aunf-title">Users Not Found</h2>
                <p className="aunf-message">
                  The following users are not part of the chain. Would you like to add them to the
                  chain and assign a role?
                </p>
              </div>
            </div>

            {/* Table — wrapped in horizontal-scroll container */}
            <div className="aunf-table-container">
              <div className="aunf-table-inner">
                {/* Header row */}
                <div className="aunf-table-header">
                  {/* <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="aunf-checkbox custom-checkbox"
                  /> */}
                  {isManagedData && <p className="aunf-header-text">Chain</p>}
                  <p className="aunf-header-text">User</p>
                  <p className="aunf-header-text">Roles</p>
                </div>

                {/* Data rows */}
                {usersList?.map((user, index) => (
                  <div key={index} className="aunf-table-row">
                    {/* <input
                      type="checkbox"
                      checked={user.selected}
                      onChange={() => handleUserSelect(index)}
                      className="aunf-checkbox custom-checkbox"
                    /> */}
                    {user?.chain && (
                      <p className="aunf-cell-text">{user.chain}</p>
                    )}
                    <p className="aunf-cell-text">{user?.email}</p>
                    <div style={{ width: "100%" }}>
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(index, e.target.value)}
                        className={`aunf-select${validationErrors[index] ? " aunf-select-error" : ""}`}
                      >
                        <option value="">Select Role</option>
                        {roles
                          ?.filter((role: any) => role.role !== Constants.AB_SYSTEM_ADMIN)
                          ?.map((role: any) => (
                            <option key={role.id} value={role.role}>{role.role}</option>
                          ))}
                      </select>
                      {validationErrors[index] && (
                        <p className="aunf-error-msg">Please assign a role to this user</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* API errors */}
            {hasApiErrors && (
              <div className="aunf-api-errors">
                {errors!.map((msg: string, i: number) => (
                  <div key={i} className="aunf-api-error-item">{msg}</div>
                ))}
              </div>
            )}

            {/* No-selection error */}
            {noSelectionError && (
              <p style={{ fontSize: 13, color: "#EF4444", margin: "8px 0 0 0" }}>
                Please select at least one user with a role assigned.
              </p>
            )}

            {/* Buttons */}
            <div className="aunf-buttons">
              <button
                className="aunf-btn aunf-btn-save"
                onClick={handleSave}
                disabled={isSaving && !hasApiErrors}
              >
                {isSaving && !hasApiErrors ? "Saving..." : "Save"}
              </button>
              <button
                className="aunf-btn aunf-btn-no"
                onClick={onNo}
                disabled={isSaving}
              >
                No
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

const connecter = connect(
  (state: State) => ({ roles: state.chains_users_role.roles }),
  { getRoles }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(AddUsersNotFoundModal);